import React, {useRef} from 'react';
import {ReactHookFormTextField} from '@netx/core-components';
import {type UseFormReturn} from 'react-hook-form';
import {type IAsset} from '@netx/core-asset-next/schema';
import {type IAssetView} from '@netx/core-asset-views/schema';
import {GenericImage} from '@netx/core-components/GenericImage';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {ReadOnlyTextField} from '@netx/core-components/TextField';
import {styled} from '@netx/core-components/Theme';
import {bytesToString} from '@netx/core-next/utils/string';
import {canEditAssetViews} from '@netx/core-asset-views/enablers/canEditAssetView';
import {SubmitButton} from '@netx/core-components/SubmitButton';

const AssetViewsDetailRoot = styled('div')(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
}));

const AssetViewsDetailPreview = styled(GenericImage)(({theme}) => ({
	flex: '1 1 auto',
	backgroundColor: theme.palette.custom.background.accent as string,
	overflow: 'hidden',
	padding: theme.spacing(2),
	'& [class*="custom-icon"]': {
		maxWidth: 500,
		maxHeight: 500,
		margin: 'auto',
		overflow: 'hidden',
		padding: theme.spacing(1),
		'& .thumbnail': {
			borderRadius: 8,
		},
	},
}));

const AssetViewsDetailAttributesWrapper = styled(ScrollBorder)(({theme}) => ({
	width: 265,
	[`&.${scrollBorderClasses.scrollWrapper}`]: {
		flex: '1 1 auto',
		padding: theme.spacing(1, 0),
	},
}));

const AssetViewsDetailDrawer = styled('div', {
	slot: 'Drawer',
})(({theme}) => ({
	flex: '0 0 auto',
	overflow: 'hidden',
	minHeight: 0,
	borderLeft: `1px solid ${theme.palette.custom.border}`,
	display: 'flex',
	flexDirection: 'column',
	zIndex: 4,
}));

const AssetViewsDetailContent = styled('div')(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	overflow: 'hidden',
	width: '100%',
	borderTop: `1px solid ${theme.palette.custom.border}`,
	position: 'relative',
}));

const AssetViewDetailTextFieldWrapper = styled('div')(({theme}) => ({
	padding: theme.spacing(1.25, 1.5),
}));

const AssetViewsDetailDrawerFooter = styled('div')(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	width: '100%',
	padding: theme.spacing(1.5),
}));

export type AssetViewsDetailProps = {
	loading?: boolean;
	asset?: IAsset;
	hidePager?: boolean;
	form?: UseFormReturn<any>;
	index: number;
	total: number;
	view?: IAssetView;
	onPrevious: () => void;
	onNext: () => void;
	onSubmit?: (event: any) => void;
};

export const AssetViewsDetailPrimitive = ({
	asset,
	form,
	loading,
	view,
	onSubmit,
}: AssetViewsDetailProps) => {
	const overflowParent = useRef<HTMLDivElement>(null);

	const canEdit = onSubmit != null && form != null && canEditAssetViews(asset);

	return (
		<AssetViewsDetailRoot>
			<AssetViewsDetailContent ref={overflowParent}>
				<AssetViewsDetailPreview
					loading={!view || !asset || loading}
					src={
						view
							? !view.thumb
								? `/icons/${view.fileTypeLabel}.png`
								: // TODO: this is a hack to get us a higher quaility preview
									view.thumb.replace(/\/thumbnail$/, '')
							: ''
					}
					alt={'view preview'}
				/>
				<AssetViewsDetailDrawer>
					<AssetViewsDetailAttributesWrapper limitContents>
						{canEdit ? (
							<AssetViewDetailTextFieldWrapper>
								<ReactHookFormTextField
									loading={!view || loading}
									control={form.control}
									label={i18n.t('i18n.name')}
									name="name"
									multiline
								/>
							</AssetViewDetailTextFieldWrapper>
						) : (
							<ReadOnlyTextField
								loading={!view || loading}
								label={i18n.t('i18n.name')}
								value={view?.name || ''}
							/>
						)}
						{canEdit ? (
							<AssetViewDetailTextFieldWrapper>
								<ReactHookFormTextField
									loading={!view || loading}
									control={form.control}
									label={i18n.t('i18n.description')}
									name="description"
									multiline
								/>
							</AssetViewDetailTextFieldWrapper>
						) : (
							<ReadOnlyTextField
								loading={!view || loading}
								label={i18n.t('i18n.description')}
								value={view?.description || ''}
							/>
						)}
						<ReadOnlyTextField
							loading={!view || loading}
							label={i18n.t('i18n.creationDate')}
							value={view?.creationDate || ''}
						/>
						<ReadOnlyTextField
							loading={!view || loading}
							label={i18n.t('i18n.fileType')}
							value={view?.fileTypeLabel || ''}
						/>
						<ReadOnlyTextField
							loading={!view || loading}
							label={i18n.t('i18n.fileSize')}
							value={bytesToString(view?.fileSize ?? 0)}
						/>
						<ReadOnlyTextField
							loading={!view || loading}
							label={i18n.t('i18n.dimensions')}
							value={view ? `${view.fileWidth} x ${view.fileHeight}` : ''}
						/>
					</AssetViewsDetailAttributesWrapper>
					{canEdit && (
						<AssetViewsDetailDrawerFooter>
							<SubmitButton
								fullWidth
								disabled={
									form.formState.isSubmitting ||
									!form.formState.isDirty ||
									!form.formState.isValid ||
									!view ||
									loading
								}
								form="asset-view-form"
								label={i18n.t('i18n.save')}
								onClick={(event) => {
									onSubmit(event);
								}}
							/>
						</AssetViewsDetailDrawerFooter>
					)}
				</AssetViewsDetailDrawer>
			</AssetViewsDetailContent>
		</AssetViewsDetailRoot>
	);
};

export const AssetViewsDetail = AssetViewsDetailPrimitive;

export default AssetViewsDetail;
